import { useState, useEffect, useMemo, useCallback } from 'react';
import { IconRefresh, IconSearch, IconPackage, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { StatCard } from '../components/cards';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface StockComparison {
  productId: number;
  productTitle: string;
  productType: string | null;
  shopifyStock: number;
  dbStock: number;
  dbSold: number;
  dbTotal: number;
  difference: number;
  locationId: number;
  locationName: string;
  price: string;
}

interface StockComparisonResponse {
  esim: StockComparison[];
  physical: StockComparison[];
}

type ShopifyStore = 'CRAVE_CONNECT' | 'AMANDLA_MOBILE' | 'MEGSAPP' | 'FUNDI_MOBILE' | 'WOW_MOBILE';

const STORE_NAMES: Record<ShopifyStore, string> = {
  CRAVE_CONNECT: 'Crave Connect',
  AMANDLA_MOBILE: 'Amandla Mobile',
  MEGSAPP: 'Megsapp',
  FUNDI_MOBILE: 'Fundi Mobile',
  WOW_MOBILE: 'Wow Mobile',
};

function StockTable({
  comparisons,
  title,
  showDbComparison,
}: {
  comparisons: StockComparison[];
  title: string;
  showDbComparison: boolean;
}) {
  if (comparisons.length === 0) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <MainCard className="text-center">
          <IconPackage className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No {title.toLowerCase()} products found</p>
        </MainCard>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-4">{title} ({comparisons.length} products)</h3>
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">Shopify Stock</TableHead>
              {showDbComparison && <TableHead className="text-right">DB Stock</TableHead>}
              {showDbComparison && <TableHead className="text-right">DB Sold</TableHead>}
              {showDbComparison && <TableHead className="text-right">DB Total</TableHead>}
              {showDbComparison && <TableHead className="text-right">Difference</TableHead>}
              <TableHead className="text-right">Price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comparisons.map((item, index) => (
              <TableRow
                key={`${item.productId}-${item.locationId}-${index}`}
                className={cn(
                  'hover:bg-muted/50',
                  showDbComparison && item.difference !== 0 && (
                    item.difference > 0 ? 'bg-orange-50' : 'bg-red-50'
                  ),
                )}
              >
                <TableCell>
                  <p className="text-sm font-medium">{item.productTitle}</p>
                  <p className="text-xs text-muted-foreground">ID: {item.productId}</p>
                </TableCell>
                <TableCell>{item.locationName}</TableCell>
                <TableCell className="text-right">
                  <span className="text-sm font-medium">{item.shopifyStock.toLocaleString()}</span>
                </TableCell>
                {showDbComparison && (
                  <TableCell className="text-right">
                    <span className="text-sm font-medium">{item.dbStock.toLocaleString()}</span>
                  </TableCell>
                )}
                {showDbComparison && (
                  <TableCell className="text-right">
                    <span className="text-sm font-medium text-muted-foreground">{item.dbSold.toLocaleString()}</span>
                  </TableCell>
                )}
                {showDbComparison && (
                  <TableCell className="text-right">
                    <span className="text-sm font-bold">{item.dbTotal.toLocaleString()}</span>
                  </TableCell>
                )}
                {showDbComparison && (
                  <TableCell className="text-right">
                    <span
                      className={cn(
                        'text-sm font-bold',
                        item.difference === 0 ? 'text-green-600' :
                        item.difference > 0 ? 'text-yellow-600' : 'text-destructive'
                      )}
                    >
                      {item.difference > 0 ? '+' : ''}{item.difference.toLocaleString()}
                    </span>
                  </TableCell>
                )}
                <TableCell className="text-right">
                  {item.price ? `R${parseFloat(item.price).toFixed(2)}` : 'N/A'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function ShopifyStockManagementPage() {
  const [esimComparisons, setEsimComparisons] = useState<StockComparison[]>([]);
  const [physicalComparisons, setPhysicalComparisons] = useState<StockComparison[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStore, setSelectedStore] = useState<ShopifyStore>('FUNDI_MOBILE');
  const [availableStores, setAvailableStores] = useState<ShopifyStore[]>([]);

  useEffect(() => {
    const loadAvailableStores = async () => {
      try {
        const response = await api.get<{ success: boolean; data: ShopifyStore[] }>('/shopify/stores');
        setAvailableStores(response.data.data || []);
        if (response.data.data && response.data.data.length > 0) {
          if (!response.data.data.includes(selectedStore)) {
            setSelectedStore(response.data.data[0]);
          }
        }
      } catch (err) {
        console.error('Failed to load available stores', err);
        setAvailableStores(['CRAVE_CONNECT', 'AMANDLA_MOBILE', 'MEGSAPP', 'FUNDI_MOBILE', 'WOW_MOBILE']);
      }
    };
    loadAvailableStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStockLevels = useCallback(async (store: ShopifyStore) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{ success: boolean; data: StockComparisonResponse; store: string }>(
        `/shopify/compare-stock?store=${store}`,
      );
      setEsimComparisons(response.data.data?.esim || []);
      setPhysicalComparisons(response.data.data?.physical || []);
    } catch (err: any) {
      console.error('Failed to load stock comparison', err);
      setError(
        err.response?.data?.message ||
        `Failed to load stock comparison for ${STORE_NAMES[store]}. Please check your Shopify credentials and database connection.`,
      );
      setEsimComparisons([]);
      setPhysicalComparisons([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedStore && availableStores.includes(selectedStore)) {
      loadStockLevels(selectedStore);
    }
  }, [selectedStore, availableStores, loadStockLevels]);

  const handleStoreChange = (store: string | null) => {
    if (!store) return;
    setSelectedStore(store as ShopifyStore);
    setEsimComparisons([]);
    setPhysicalComparisons([]);
  };

  const filteredEsimComparisons = useMemo(() => {
    if (!searchTerm.trim()) return esimComparisons;
    const lowerSearch = searchTerm.toLowerCase();
    return esimComparisons.filter(
      (item) =>
        item.productTitle.toLowerCase().includes(lowerSearch) ||
        item.locationName.toLowerCase().includes(lowerSearch),
    );
  }, [esimComparisons, searchTerm]);

  const filteredPhysicalComparisons = useMemo(() => {
    if (!searchTerm.trim()) return physicalComparisons;
    const lowerSearch = searchTerm.toLowerCase();
    return physicalComparisons.filter(
      (item) =>
        item.productTitle.toLowerCase().includes(lowerSearch) ||
        item.locationName.toLowerCase().includes(lowerSearch),
    );
  }, [physicalComparisons, searchTerm]);

  const esimSummary = useMemo(() => {
    const totalProducts = new Set(filteredEsimComparisons.map((item) => item.productId)).size;
    const totalShopifyStock = filteredEsimComparisons.reduce((sum, item) => sum + item.shopifyStock, 0);
    const totalDbStock = filteredEsimComparisons.reduce((sum, item) => sum + item.dbStock, 0);
    const totalDbSold = filteredEsimComparisons.reduce((sum, item) => sum + item.dbSold, 0);
    const totalDbTotal = filteredEsimComparisons.reduce((sum, item) => sum + item.dbTotal, 0);
    const totalDifference = filteredEsimComparisons.reduce((sum, item) => sum + item.difference, 0);
    const mismatchedProducts = filteredEsimComparisons.filter((item) => item.difference !== 0).length;
    return { totalProducts, totalShopifyStock, totalDbStock, totalDbSold, totalDbTotal, totalDifference, mismatchedProducts };
  }, [filteredEsimComparisons]);

  const physicalSummary = useMemo(() => {
    const totalProducts = new Set(filteredPhysicalComparisons.map((item) => item.productId)).size;
    const totalStock = filteredPhysicalComparisons.reduce((sum, item) => sum + item.shopifyStock, 0);
    return { totalProducts, totalStock };
  }, [filteredPhysicalComparisons]);

  return (
    <div>
      <PageHeader
        title="Shopify Stock Comparison"
        subtitle={selectedStore
          ? `Comparing Shopify vs Database stock for ${STORE_NAMES[selectedStore]}`
          : 'Compare stock levels between Shopify and database'}
        action={
          <Button onClick={() => loadStockLevels(selectedStore)} disabled={isLoading}>
            <IconRefresh className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <MainCard className="mb-6">
        <Label className="mb-1.5 block text-sm">Select Store</Label>
        <Select value={selectedStore} onValueChange={handleStoreChange} disabled={isLoading}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableStores.map((store) => (
              <SelectItem key={store} value={store}>
                {STORE_NAMES[store]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </MainCard>

      {esimComparisons.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">eSIM Products Summary</h3>
          <div className="flex gap-4 mb-6">
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">Total Products</p><p className="text-3xl font-bold">{esimSummary.totalProducts}</p></CardContent></Card>
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">Shopify Stock</p><p className="text-3xl font-bold">{esimSummary.totalShopifyStock.toLocaleString()}</p></CardContent></Card>
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">DB Stock</p><p className="text-3xl font-bold">{esimSummary.totalDbStock.toLocaleString()}</p></CardContent></Card>
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">DB Sold</p><p className="text-3xl font-bold text-muted-foreground">{esimSummary.totalDbSold.toLocaleString()}</p></CardContent></Card>
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">DB Total</p><p className="text-3xl font-bold">{esimSummary.totalDbTotal.toLocaleString()}</p></CardContent></Card>
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">Difference</p><p className={cn('text-3xl font-bold', esimSummary.totalDifference === 0 ? '' : esimSummary.totalDifference > 0 ? 'text-yellow-600' : 'text-destructive')}>{esimSummary.totalDifference > 0 ? '+' : ''}{esimSummary.totalDifference.toLocaleString()}</p></CardContent></Card>
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">Mismatched Products</p><p className={cn('text-3xl font-bold', esimSummary.mismatchedProducts > 0 ? 'text-destructive' : '')}>{esimSummary.mismatchedProducts}</p></CardContent></Card>
          </div>
        </div>
      )}

      {physicalComparisons.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Physical SIM Products Summary</h3>
          <div className="flex gap-4 mb-6">
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">Total Products</p><p className="text-3xl font-bold">{physicalSummary.totalProducts}</p></CardContent></Card>
            <Card className="flex-1"><CardContent className="pt-6"><p className="text-sm text-muted-foreground mb-1">Total Stock</p><p className="text-3xl font-bold">{physicalSummary.totalStock.toLocaleString()}</p></CardContent></Card>
          </div>
        </div>
      )}

      <MainCard className="mb-6">
        <div className="relative">
          <IconSearch className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by product name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </MainCard>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-sm underline ml-4">Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex justify-center p-8">
          <IconLoader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {!isLoading && !error && (
        <>
          <StockTable
            comparisons={filteredEsimComparisons}
            title="eSIM Products (with DB Comparison)"
            showDbComparison={true}
          />
          <Separator className="my-8" />
          <StockTable
            comparisons={filteredPhysicalComparisons}
            title="Physical SIM Products (Shopify Only)"
            showDbComparison={false}
          />
        </>
      )}
    </div>
  );
}
